import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { Resource } from './resource';
export { Resource };
import { resourceSchema } from '@schemas/business-objects/resource';
import { ImageFormArray } from './image-form-group';
import { PdfDocumentFormGroup } from './pdf-document-form-group';
import { ResourceCategoryItemFormArray } from './resource-category-item-form-group';
import { ResourceSentenceFormArray } from './resource-sentence-form-group';
import { SessionActivityItemFormArray } from './session-activity-item-form-group';
import { TextBoxFormArray } from './text-box-form-group';
import { UserFormGroup } from './user-form-group';
import type { ExportImportConfig } from '../interface-definitions/index';

export class ResourceFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    type: SchemaFormControl;
    instructions: SchemaFormControl;
    locked: SchemaFormControl;
    uuid: SchemaFormControl;
    version: SchemaFormControl;
    status: SchemaFormControl;
    createdAt: SchemaFormControl;
    publishedAt: SchemaFormControl;
    archivedAt: SchemaFormControl;
    categoryItems: ResourceCategoryItemFormArray;
    pdfDocument: PdfDocumentFormGroup;
    images: ImageFormArray;
    sessionActivityItems: SessionActivityItemFormArray;
    textBoxes: TextBoxFormArray;
    sentences: ResourceSentenceFormArray;
    createdById: SchemaFormControl;
    createdBy: UserFormGroup;
    publishedById: SchemaFormControl;
    publishedBy: UserFormGroup;
    archivedById: SchemaFormControl;
    archivedBy: UserFormGroup;
  };

  declare value: Partial<Resource>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(resourceSchema, Resource, data, options);
  }

  get object(): Resource {
    return new Resource(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'categoryItems':
        return new ResourceCategoryItemFormArray();
      case 'pdfDocument':
        return new PdfDocumentFormGroup();
      case 'images':
        return new ImageFormArray();
      case 'sessionActivityItems':
        return new SessionActivityItemFormArray();
      case 'textBoxes':
        return new TextBoxFormArray();
      case 'sentences':
        return new ResourceSentenceFormArray();
      case 'createdBy':
        return new UserFormGroup();
      case 'publishedBy':
        return new UserFormGroup();
      case 'archivedBy':
        return new UserFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<Resource[]> {
    return Resource.find(filter);
  }

  static async findOne(filter?: any): Promise<Resource | null> {
    return Resource.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<Resource> {
    return Resource.findById(id, filter);
  }

  static async create(data: Partial<Resource>): Promise<Resource> {
    return Resource.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<Resource>,
  ): Promise<Resource> {
    return Resource.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return Resource.deleteById(id);
  }

  async export(): Promise<any> {
    const instance = new Resource(this.getRawValue());
    return instance.export();
  }

  static async exportMany(body: any): Promise<any> {
    return Resource.exportMany(body);
  }

  async publish(): Promise<any> {
    const instance = new Resource(this.getRawValue());
    return instance.publish();
  }

  async newVersion(): Promise<any> {
    const instance = new Resource(this.getRawValue());
    return instance.newVersion();
  }

  async latestVersion(): Promise<any> {
    const instance = new Resource(this.getRawValue());
    return instance.latestVersion();
  }

  static async findLatestVersions(
    filter: any,
    publishedOnly?: boolean,
  ): Promise<any[]> {
    return Resource.findLatestVersions(filter, publishedOnly);
  }

  async archive(): Promise<any> {
    const instance = new Resource(this.getRawValue());
    return instance.archive();
  }

  async dataForCopy(): Promise<any> {
    const instance = new Resource(this.getRawValue());
    return instance.dataForCopy();
  }
}

export class ResourceFormArray extends PersistedFormArray {
  readonly entityName = 'Resource' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(resourceSchema, Resource, options);
  }

  protected override createItemGroup() {
    return new ResourceFormGroup();
  }

  get array(): Resource[] {
    return this.controls.map((group: ResourceFormGroup) => group.object);
  }
}

export class ResourcePersistedArray extends PersistedArray<Resource> {
  readonly entityName = 'Resource' as const;

  constructor(options?: PersistedArrayOptions) {
    super(Resource, options);
  }
}
